// src/features/quotation/components/QuotationItemForm.jsx
import React from "react";
import {
  Grid,
  TextField,
  IconButton,
  Box,
  Typography,
  Paper,
} from "@mui/material";
import { Delete, Add } from "@mui/icons-material";

const QuotationItemForm = ({
  items = [],
  onItemChange,
  onAddItem,
  onRemoveItem,
  readOnly = false,
}) => {
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };

    // Recalculate item total
    const quantity = parseFloat(newItems[index].quantity) || 0;
    const unitPrice = parseFloat(newItems[index].unitPrice) || 0;
    newItems[index].total = quantity * unitPrice;

    onItemChange && onItemChange(newItems);
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Quotation Items
        </Typography>
        {!readOnly && (
          <IconButton onClick={onAddItem} color="primary" size="small">
            <Add />
          </IconButton>
        )}
      </Box>

      {items.map((item, index) => (
        <Paper
          key={item.id || index}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: index % 2 === 0 ? "#fafafa" : "white",
            border: "1px solid #f0f0f0",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Description"
                value={item.description || ""}
                onChange={(e) =>
                  handleItemChange(index, "description", e.target.value)
                }
                size="small"
                disabled={readOnly}
                required
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={item.quantity || 1}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "quantity",
                    parseFloat(e.target.value) || 0,
                  )
                }
                size="small"
                disabled={readOnly}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Unit Price"
                type="number"
                value={item.unitPrice || 0}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "unitPrice",
                    parseFloat(e.target.value) || 0,
                  )
                }
                size="small"
                disabled={readOnly}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <span>₹</span>,
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Discount %"
                type="number"
                value={item.discount || 0}
                onChange={(e) =>
                  handleItemChange(
                    index,
                    "discount",
                    parseFloat(e.target.value) || 0,
                  )
                }
                size="small"
                disabled={readOnly}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={4} md={1}>
              <Typography variant="body2" fontWeight="bold" align="right">
                ₹{(item.total || 0).toFixed(2)}
              </Typography>
            </Grid>
            {!readOnly && (
              <Grid item xs={2} md={1}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveItem && onRemoveItem(index)}
                  disabled={items.length === 1}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Grid>
            )}
          </Grid>

          {item.notes && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Note: {item.notes}
              </Typography>
            </Box>
          )}
        </Paper>
      ))}

      {/* Subtotal */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 2,
          p: 2,
          bgcolor: "#f5f5f5",
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          Subtotal: ₹{calculateSubtotal().toFixed(2)}
        </Typography>
      </Box>
    </Box>
  );
};

export default QuotationItemForm;
